import { Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ChangeDetectorRef, ViewChild, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { Form1118ConfigService } from './form-1118-config.service';
import * as _ from 'lodash-es';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';

@Component({
    selector: 'gtw-form-1118-config',
    templateUrl: './form-1118-config.component.html',
    styleUrls: ['./form-1118-config.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Form1118ConfigComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // 🚀 CRITICAL: Filter params input for dynamic data
  filterParams: any;
  isEdit = false;

  rowData: any;

  private isInitialized = false;

  // Component properties
  globalService: any;
  USER_SETTINGS: any;
  SERVER_CONFIG: any;
  GENERAL_CONFIG: any;
  
  minimise: boolean = false;
  isSaveClicked: boolean = false;
  crudLoading: boolean = false;
  
  // Form data properties
  filterTaxYear: string = '';
  gl_filing_key: string = '';
  curentyear: string = '';
  scenario: string = '';
  jcd_key: string = '';
  
  filingDetails: any[] = [];
  filingEnityDetails: any[] = [];
  filingAllDetails: any[] = [];
  filerEntityEdit: any = null; // Initialize as null to match option structure
  filerEntity: any[] = [];
  selectedFilerEntityKeys: string[] = []; // For multi-select binding
  
  // Searchable multi-select dropdown properties
  @ViewChild('filerEntityDropdown') filerEntityDropdownRef: ElementRef<HTMLElement> | null = null;
  filerEntityDropdownOpen: boolean = false;
  filerEntitySearchText: string = '';
  filerEntityFilteredData: any[] = [];
  configNameDetails: any[] = [];
  configNameDetailsCopy: any[] = [];
  configName: any = {};
  configValue: any = {};
  checkboxOrSelectValue: boolean = false;
  gotExistingData: any[] = [];
  is_locked: any = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private form1118ConfigService: Form1118ConfigService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.globalService = { ...(header.globalParams || {}) };
    this.filterParams = normalizeFilterParams(activeScreen);
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : row;
    this.isEdit = !!(this.rowData && Object.keys(this.rowData).length > 0 && this.rowData.MAP_KEY);
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
      this.isInitialized = true;
      this.init();
    }
  }

  init(): void {
    try {
      if (this.filterParams && Object.keys(this.filterParams).length > 0 &&
          this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
        this.initializeData();
      }
    } catch (e) {
      console.error('Error in form-1118-config init:', e);
    }
  }

  initializeData(): void {
    // Get filter params
    this.filterTaxYear = this.filterParams.tax_year || this.globalService.tax_year || '';
    this.gl_filing_key = this.filterParams.filing_group_key || '';
    this.curentyear = this.globalService.tax_year || '';
    this.scenario = this.globalService.scenario || '';
    this.jcd_key = this.globalService.jcd_key || '';
    
    // Set checkbox value based on rowData (only in edit mode)
    this.checkboxOrSelectValue = this.isEdit && this.rowData?.CONFIG_VALUE === "Yes" ? true : false;
    
    // Check lock status
    this.checkLockStatus();
    
    // Load initial data
    this.configNameInfo();
    this.getFillingGroupInfo();
    this.getSavedConfigData();
  }

  checkLockStatus(): void {
    this.form1118ConfigService.checkLockStatus(
      this.GENERAL_CONFIG.base_url,
      this.scenario,
      this.jcd_key,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.is_locked = data.jsonObject;
        if (this.is_locked != undefined && this.is_locked != null && 
            this.is_locked[0]?.LOCK_STATUS == 'Y') {
          this.cancel();
          this.gtwSnackbarService.openSnackBar(
            `Tax Return has already been filed for tax year ${this.filterTaxYear}.`,
            'gtw-snackbar--red'
          );
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  configNameInfo(): void {
    this.form1118ConfigService.getConfigNameInfo(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          let nameDetails = response.jsonObject;
          nameDetails.forEach((item: any) => {
            if(item.CONFIG_VALUE && item.CONFIG_VALUE === "Yes") {
              item.checkboxOrSelectValue = true;
            }
          });
          
          this.configNameDetails = nameDetails;
          this.configNameDetailsCopy = nameDetails;
          
          if (this.isEdit) {
            const found = _.find(this.configNameDetails, (p: any) => p.MAP_KEY === this.rowData.MAP_KEY);
            if (found) {
              this.configName = found;
            }
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading config name info:', error);
        this.gtwSnackbarService.openSnackBar('Error loading schedule data', 'gtw-snackbar--red');
      }
    );
  }

  getFillingGroupInfo(): void {
    this.form1118ConfigService.getFilingGroupInfo(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          this.filingAllDetails = Object.assign([], response.jsonObject);
          this.filingDetails = Object.assign([], response.jsonObject);
          this.filingDetails = _.uniqBy(this.filingDetails, 'CONSOL_GROUP_KEY');
          this.filingEnityDetailInfo();
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading filing group info:', error);
        this.gtwSnackbarService.openSnackBar('Error loading filer entity data', 'gtw-snackbar--red');
      }
    );
  }

  filingEnityDetailInfo(): void {
    this.filingEnityDetails = Object.assign([], this.filingAllDetails);
    this.filingEnityDetails.unshift({"FILER_LE_KEY":"","FILER_LE":"SELECT"});
    
    if (this.isEdit && this.rowData && this.rowData.FILER_LE_KEY) {
      // Find matching entity by FILER_LE_KEY - convert both to strings for comparison
      const rowDataKey = String(this.rowData.FILER_LE_KEY);
      const found = _.find(this.filingEnityDetails, (p: any) => {
        return p.FILER_LE_KEY && String(p.FILER_LE_KEY) === rowDataKey;
      });
      if (found) {
        this.filerEntityEdit = found;
      } else {
        // If no match found, set to null to show SELECT option
        this.filerEntityEdit = null;
      }
    } else {
      this.filerEntity = [];
      this.selectedFilerEntityKeys = [];
      // Initialize filtered data for searchable dropdown
      this.filerEntityFilteredData = this.filingEnityDetails.filter((item: any) => item.FILER_LE_KEY !== '');
    }
  }

  getSavedConfigData(): void {
    this.form1118ConfigService.getSavedConfigData(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          this.gotExistingData = response.jsonObject;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading saved config data:', error);
      }
    );
  }

  reset(): void {
    if(!this.isEdit) {
      this.filingEnityDetails = Object.assign([], this.filingAllDetails);
      this.filingEnityDetails.unshift({"FILER_LE_KEY":"","FILER_LE":"SELECT"});
      this.filerEntity = [];
      this.selectedFilerEntityKeys = [];
      this.configName = {};
      this.checkboxOrSelectValue = false;
      // Reset searchable dropdown state
      this.filerEntityDropdownOpen = false;
      this.filerEntitySearchText = '';
      this.filerEntityFilteredData = this.filingEnityDetails.filter((item: any) => item.FILER_LE_KEY !== '');
    } else {
      if (this.rowData && this.rowData.FILER_LE_KEY) {
        // Find matching entity by FILER_LE_KEY - convert both to strings for comparison
        const rowDataKey = String(this.rowData.FILER_LE_KEY);
        const foundEntity = _.find(this.filingEnityDetails, (p: any) => {
          return p.FILER_LE_KEY && String(p.FILER_LE_KEY) === rowDataKey;
        });
        if (foundEntity) {
          this.filerEntityEdit = foundEntity;
        } else {
          // If no match found, set to null to show SELECT option
          this.filerEntityEdit = null;
        }
      } else {
        this.filerEntityEdit = null;
      }
      
      const foundConfig = _.find(this.configNameDetails, (p: any) => p.MAP_KEY === this.rowData.MAP_KEY);
      if (foundConfig) {
        this.configName = foundConfig;
      }
      
      this.checkboxOrSelectValue = this.rowData.CONFIG_VALUE === "Yes" ? true : false;
    }
  }

  save(): void {
    // To Block user from saving for Return filed year
    if (this.is_locked != undefined && this.is_locked != null && 
        this.is_locked[0]?.LOCK_STATUS == 'Y') {
      this.cancel();
      this.gtwSnackbarService.openSnackBar(
        "You cannot perform this action as Tax return is already filed.",
        'gtw-snackbar--red'
      );
      return;
    }

    this.isSaveClicked = true;
    
    // Convert selected keys to entity objects for create mode
    if (!this.isEdit && this.selectedFilerEntityKeys && this.selectedFilerEntityKeys.length > 0) {
      // Convert selected keys to strings for comparison (FILER_LE_KEY might be number or string)
      const selectedKeysAsStrings = this.selectedFilerEntityKeys.map(k => String(k));
      this.filerEntity = this.filingEnityDetails.filter((item: any) => {
        if (!item.FILER_LE_KEY || item.FILER_LE_KEY === '') {
          return false;
        }
        const itemKey = String(item.FILER_LE_KEY);
        return selectedKeysAsStrings.includes(itemKey);
      });
    }
    
    // Validation
    if ((!this.isEdit && this.filerEntity && (this.filerEntity.length == 0 || 
        this.configName.MAP_KEY === undefined || this.configName.MAP_KEY === null )) || 
        (this.isEdit && this.filerEntityEdit && (this.filerEntityEdit.FILER_LE_KEY == "" 
          || this.filerEntityEdit.FILER_LE_KEY === undefined || this.filerEntityEdit.FILER_LE_KEY == null || 
          this.configName.MAP_KEY === undefined || this.configName.MAP_KEY === null))) {
      this.gtwSnackbarService.openSnackBar("Please select/enter all the required fields.", 'gtw-snackbar--red');
      this.isSaveClicked = false;
      return;
    }
    
    if (!this.isEdit) {
      let flag1 = false;
      this.filerEntity.forEach((fEntityInfo: any) => {
        if(fEntityInfo.FILER_LE == 'SELECT') {
          flag1 = true;
        }
      });
      
      if(flag1){
        this.gtwSnackbarService.openSnackBar("Please select/enter all the required fields.", 'gtw-snackbar--red');
        this.isSaveClicked = false;
        return;
      }
    }
    
    // Check for duplicates
    if (!this.isEdit) {
      let flag = false;
      this.filerEntity.forEach((fEntityInfo: any) => {
        if(_.find(this.gotExistingData, {'MAP_KEY': this.configName.MAP_KEY,
          'FILER_LE': fEntityInfo.FILER_LE})) {
          flag = true;
        }
      });
      
      if(flag){
        this.gtwSnackbarService.openSnackBar("Form 1118 Config setup already exist.", 'gtw-snackbar--red');
        this.isSaveClicked = false;
        return;
      }
    } else {
      let flag = false;
      if(_.find(this.gotExistingData, {'MAP_KEY': this.configName.MAP_KEY,
        'FILER_LE': this.filerEntityEdit.FILER_LE,
        'CONFIG_VALUE': this.checkboxOrSelectValue ? "Yes" : "No"})) {
        flag = true;
      }
      
      if(flag){
        this.gtwSnackbarService.openSnackBar("Form 1118 Config setup already exist.", 'gtw-snackbar--red');
        this.isSaveClicked = false;
        return;
      }
    }

    // Build save data
    const clobSettingsObj: any = {};
    clobSettingsObj['sso_id'] = this.USER_SETTINGS?.user?.sso_id || '';
    clobSettingsObj["TAX_YEAR"] = this.filterTaxYear;

    const returnObj: any = {};
    returnObj["TAX_YEAR"] = this.filterTaxYear;
    returnObj['ADJ_KEY'] = !this.isEdit ? null : this.rowData.ADJ_KEY;
    returnObj['FILER_LE_KEY'] = !this.isEdit ? _.map(this.filerEntity, 'FILER_LE_KEY').join() : this.filerEntityEdit.FILER_LE_KEY;
    returnObj['MAP_KEY'] = this.configName.MAP_KEY;
    returnObj['CONFIG_VALUE'] = this.checkboxOrSelectValue ? "Yes" : "No";
    returnObj["OPERATION_TYPE"] = !this.isEdit ? "I" : "U";
    returnObj['FILING_GROUP_KEY'] = this.gl_filing_key;
    returnObj['TRANS_TYPE'] = this.configName.TRANS_TYPE;

    const clobDataDetails: any[] = [];
    clobDataDetails.push(returnObj);
    console.log("saveDataDetails  -- ", clobDataDetails);

    const message = "Form 1118 Config have been saved/updated";
    
    if (this.isSaveClicked) {
      this.sendDetails(clobSettingsObj, clobDataDetails, message, clobSettingsObj);
    }
  }

  sendDetails(returnObj: any, details: any[], message: string, otherInfo: any): void {
    this.crudLoading = true;
    
    this.form1118ConfigService.save(
      this.GENERAL_CONFIG.base_url,
      returnObj,
      details,
      otherInfo,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;
        console.log(" result", result);
        
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          this.isSaveClicked = false;
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close automatically
          this.isSaveClicked = false;
        }
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  cancel(): void {
    // Following pattern - dispatch CustomEvent to close modal
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-form-1118-config-ce')) {
      document.querySelector('gtw-form-1118-config-ce')?.dispatchEvent(updateEvent);
    }
  }

  // Helper method for comparing select options
  compareConfigOptions(option1: any, option2: any): boolean {
    if (option1 == null && option2 == null) return true;
    if (option1 == null || option2 == null) return false;
    return option1 && option2 ? option1.MAP_KEY === option2.MAP_KEY : option1 === option2;
  }

  compareFilerEntityOptions(option1: any, option2: any): boolean {
    if (option1 == null && option2 == null) return true;
    if (option1 == null || option2 == null) return false;
    // Compare by FILER_LE_KEY if both are objects
    if (option1 && option2 && typeof option1 === 'object' && typeof option2 === 'object') {
      // Convert both to strings for comparison to handle number/string mismatches
      const key1 = option1.FILER_LE_KEY ? String(option1.FILER_LE_KEY) : '';
      const key2 = option2.FILER_LE_KEY ? String(option2.FILER_LE_KEY) : '';
      return key1 === key2 && key1 !== '';
    }
    return option1 === option2;
  }

  // Searchable multi-select dropdown methods
  filterFilerEntityDropdown(): void {
    if (!this.filingEnityDetails || this.filingEnityDetails.length === 0) {
      this.filerEntityFilteredData = [];
      return;
    }
    
    // Filter out the "SELECT" option (empty FILER_LE_KEY)
    let filtered = this.filingEnityDetails.filter((item: any) => item.FILER_LE_KEY !== '');
    
    // Apply search filter if search text exists
    if (this.filerEntitySearchText && this.filerEntitySearchText.trim() !== '') {
      const searchLower = this.filerEntitySearchText.toLowerCase().trim();
      filtered = filtered.filter((item: any) => {
        const displayValue = item.FILER_LE || '';
        return displayValue.toLowerCase().includes(searchLower);
      });
    }
    
    this.filerEntityFilteredData = filtered;
  }
  
  onFilerEntitySearchChange(): void {
    this.filterFilerEntityDropdown();
  }
  
  toggleFilerEntityDropdown(): void {
    this.filerEntityDropdownOpen = !this.filerEntityDropdownOpen;
    if (this.filerEntityDropdownOpen) {
      this.filerEntitySearchText = '';
      this.filterFilerEntityDropdown();
    }
  }
  
  closeFilerEntityDropdown(): void {
    this.filerEntityDropdownOpen = false;
    this.filerEntitySearchText = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.filerEntityDropdownOpen) {
      return;
    }
    const ref = this.filerEntityDropdownRef?.nativeElement;
    if (ref && event.target instanceof Node && !ref.contains(event.target)) {
      this.closeFilerEntityDropdown();
    }
  }
  
  isFilerEntitySelected(filerLeKey: string): boolean {
    // 🔴 CRITICAL: Convert both to strings for consistent comparison
    // FILER_LE_KEY might be number or string, so normalize both sides
    const key = String(filerLeKey);
    return this.selectedFilerEntityKeys.some(k => String(k) === key);
  }
  
  /**
   * Check if the click event target is a checkbox input
   * Used to prevent double-toggle when clicking checkbox
   */
  isCheckboxClick(event: any): boolean {
    const target = event.target as HTMLElement;
    return target && target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox';
  }

  toggleFilerEntitySelection(option: any): void {
    if (option.FILER_LE_KEY === '') {
      return; // Don't allow selecting the "SELECT" option
    }
    
    // Convert to string for consistent comparison
    const key = String(option.FILER_LE_KEY);
    const index = this.selectedFilerEntityKeys.findIndex(k => String(k) === key);
    if (index > -1) {
      // Unselect
      this.selectedFilerEntityKeys.splice(index, 1);
    } else {
      // Select
      this.selectedFilerEntityKeys.push(key);
    }
    // 🔴 CRITICAL: Trigger change detection to update the display
    this.cdr.detectChanges();
  }
  
  selectAllFilerEntities(): void {
    // Select all items in filtered data (excluding empty key)
    this.filerEntityFilteredData.forEach((item: any) => {
      if (item.FILER_LE_KEY !== '') {
        const key = String(item.FILER_LE_KEY);
        // Check if already selected using string comparison
        const isSelected = this.selectedFilerEntityKeys.some(k => String(k) === key);
        if (!isSelected) {
          this.selectedFilerEntityKeys.push(key);
        }
      }
    });
    // 🔴 CRITICAL: Trigger change detection to update the display
    this.cdr.detectChanges();
  }
  
  unselectAllFilerEntities(): void {
    // Unselect all items in filtered data
    this.filerEntityFilteredData.forEach((item: any) => {
      const key = String(item.FILER_LE_KEY);
      const index = this.selectedFilerEntityKeys.findIndex(k => String(k) === key);
      if (index > -1) {
        this.selectedFilerEntityKeys.splice(index, 1);
      }
    });
    // 🔴 CRITICAL: Trigger change detection to update the display
    this.cdr.detectChanges();
  }
  
  getSelectedFilerEntitiesDisplay(): string {
    if (!this.selectedFilerEntityKeys || this.selectedFilerEntityKeys.length === 0) {
      return '';
    }
    
    // 🔴 CRITICAL: Convert both to strings for consistent comparison
    // FILER_LE_KEY might be number or string, so normalize both sides
    const selectedKeysAsStrings = this.selectedFilerEntityKeys.map(k => String(k));
    const selectedEntities = this.filingEnityDetails.filter((item: any) => {
      if (!item.FILER_LE_KEY || item.FILER_LE_KEY === '') {
        return false;
      }
      const itemKey = String(item.FILER_LE_KEY);
      return selectedKeysAsStrings.includes(itemKey);
    });
    
    if (selectedEntities.length === 0) {
      return '';
    }
    
    // 🔴 CRITICAL: Display format - show entity name for 1, count for multiple
    if (selectedEntities.length === 1) {
      return selectedEntities[0].FILER_LE;
    } else {
      // Show count when more than 1 is selected (e.g., "2 selected", "3 selected")
      return `${selectedEntities.length} selected`;
    }
  }

}

